<template>
  <div v-if="products">
    <div class="relative">
      <div class="slider-title" ref="sliderTitleRef">
        <h2
          class="text-mobile-heading-2 md:text-heading-2 text-primary-1 capitalize"
        >
          {{ title }}
        </h2>
        <div
          v-if="products.length >= 8 && reference"
          class="show-more"
          @click="handleShowMore"
        >
          {{ t("seeMore") }}
          <icon-chevron class="md:hidden !mb-0 text-[16px] text-gray-1" />
        </div>
      </div>
      <BaseSwiper
        ref="recommendationSwiper"
        :key="layoutSize || 0"
        :swiperStyle="{ paddingTop: `${sliderTitleHeight + 20}px !important` }"
        class="discover-recommandation-swiper md:!pt-[75px] !pb-1 [&_.swiper-button-next]:max-md:hidden [&_.swiper-button-prev]:max-md:hidden"
        :slides-per-view="4"
        :navigation="true"
        :modules="[Navigation]"
        :loop="false"
        :space-between="20"
        :autoplay="{
          delay: 8000,
          disableOnInteraction: true,
        }"
        :breakpoints="breakpoints"
        :items="products.slice(0, 10)"
        @swiper="onSwiperInit"
      >
        <template #item="{ item }">
          <LazyExperienceCardHandler
            :key="item.id"
            :item="item"
            :card-type="type"
            :dark-mode="darkMode"
            :vertical="true"
          />
        </template>
      </BaseSwiper>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IProduct } from "../../types/general";
import { computed, onMounted, ref, type PropType } from "vue";
import { Navigation } from "swiper/modules";
import { useRoute, useRouter } from "vue-router";
import type SwiperType from "swiper/types/swiper-class";
const props = defineProps({
  products: { type: Array as PropType<IProduct[]>, required: true },
  title: {
    type: String,
    required: true,
  },
  reference: { type: Object, required: true },
  darkMode: {
    type: Boolean,
    default: false,
  },
});
const layoutSize = ref<number | null>(null);
const sliderTitleRef = ref<HTMLElement | null>(null);
const sliderTitleHeight = ref(0);
const recommendationSwiper = ref(null);
const swiperInstance = ref<SwiperType | null>(null);

const onSwiperInit = (swiper) => {
  swiperInstance.value = swiper;
};
onMounted(() => {
  const rootStyles = getComputedStyle(document.documentElement);
  const tmp = rootStyles.getPropertyValue("--layout-max-width").trim();
  layoutSize.value = parseInt(tmp);
  if (sliderTitleRef.value) {
    sliderTitleHeight.value = sliderTitleRef.value.offsetHeight;
  }

  if (swiperInstance.value) {
    swiperInstance.value.update();
  }
});
const { t } = useI18n();
const emit = defineEmits(["scroll-to-activities"]);
const type = ref(useRuntimeConfig().public.statics.productCardType);
const route = useRoute();
const router = useRouter();
const localePath = useLocalePath();
const breakpoints = computed(() => ({
  320: {
    slidesPerView: 1.4,
    slidesPerGroup: 1,
    spaceBetween: 10,
  },
  960: {
    slidesPerView: layoutSize.value && layoutSize.value < 1000 ? 3 : 4,
    slidesPerGroup: 1,
    spaceBetween: 18,
  },
}));

const handleShowMore = () => {
  // const currentQuery = { ...route.query };

  let newQuery = {};
  if (props.reference.type === "hashtag") {
    newQuery = {
      labels: props.reference.slug,
    };
  }

  if (props.reference.type === "category") {
    newQuery = {
      category: props.reference.slug,
    };
  }

  if (Object.keys(newQuery).length) {
    router.push(localePath({ name: "things-to-do-in-slug", query: newQuery }));
    emit("scroll-to-activities");
  }
};
</script>
<style lang="scss" scoped>
.slider-title {
  @apply w-full md:w-[calc(100%-100px)] flex justify-between items-end md:items-center gap-4 
  mb-5 md:mb-[30px] md:absolute ltr:left-0 rtl:right-0 top-0;
}
.dark-mode {
  h2,
  .show-more,
  .show-more svg {
    @apply text-title-color;
  }
}
.show-more {
  @apply normal-case text-mobile-paragraph-1 md:text-paragraph-1 cursor-pointer text-gray-1 
  md:underline underline-offset-4 text-nowrap flex items-center;
}
</style>
